import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { showResult } from 'infrastructure/hooks/utils';
import { Assignee } from 'infrastructure/enums';

import { apiUrlGenerator } from '../../use-api';

import type { ParentClinicOption } from 'infrastructure/enums/parent-clinic';
import type { ActionTimesQuota, ReadingType } from 'infrastructure/enums';
import type { IPartialPaginationMeta } from 'infrastructure/interfaces';
import type {
  IRpmActivityCountResponse,
  IRpmPatientActivity,
} from 'infrastructure/interfaces/i-rpm-patient-activity';

export type TDashboardActivityFilter = {
  reviewed?: boolean;
  applySpecialFilter: boolean;
  startTime?: number;
  endTime?: number;
  clinicGuid?: string;
  parentClinicOption?: ParentClinicOption;
  childClinic?: boolean;
  pmpmStatus?: boolean;
  patientGuid?: string;
  clinicState?: string;
  readingType?: ReadingType;
  actionTimesQuota?: ActionTimesQuota;
  doctorGuid?: string[];
  insuranceType?: string;
  rangeLabel?: string;
  assignee?: Assignee;
  status?: string;
  name?: string;
  slaReadings?: string;
  reverse?: boolean;
  skipMACheck?: boolean;
} & IPartialPaginationMeta;

export const DASHBOARD_ACTIVITY_PER_PAGE = 100;

export type TDashboardActivityFilterWithDoctorString = Omit<
  TDashboardActivityFilter,
  'doctorGuid'
> & {
  doctorGuid?: string;
};

export type TRpmDashboardActivityQueryKey = [
  string,
  TDashboardActivityFilterWithDoctorString,
];

const getRpmActivity = async (
  filter: TDashboardActivityFilterWithDoctorString,
) => {
  const url = `core/patients/readings`;
  const { data, error } = await dataService.getList<IRpmPatientActivity>(
    apiUrlGenerator(url, filter),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const getActivityCountFn = async (
  filter: TDashboardActivityFilterWithDoctorString,
) => {
  const url = 'core/patients/readings/count';
  const { data, error } = await dataService.getList<IRpmActivityCountResponse>(
    apiUrlGenerator(url, filter),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const getRpmActivityKey = (
  filter: TDashboardActivityFilterWithDoctorString,
): TRpmDashboardActivityQueryKey => ['getRpmActivity', filter];
export const getRpmActivityCountKey = (
  filter: TDashboardActivityFilterWithDoctorString,
) => ['getRpmActivityCount', filter];

export const useRpmActivity = (
  filter: TDashboardActivityFilter,
  pageSize?: number,
) => {
  const filterWithItems: TDashboardActivityFilterWithDoctorString = {
    ...filter,
    assignee: filter.assignee === Assignee.All ? undefined : filter.assignee,
    doctorGuid: filter.doctorGuid?.join(','),
    items: pageSize || DASHBOARD_ACTIVITY_PER_PAGE,
    clinicGuid: filter.clinicGuid,
    pmpmStatus: filter.pmpmStatus ?? undefined,
    parentClinicOption:
      filter.parentClinicOption && filter.clinicGuid
        ? filter.parentClinicOption
        : undefined,
  };

  const queryKey = getRpmActivityKey(filterWithItems);

  const {
    data: activity,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: async () => getRpmActivity(filterWithItems),
    enabled: Boolean(filter.startTime || filter.endTime),
    keepPreviousData: true,
  });

  const { data: activityCount, isLoading: countLoading } = useQuery({
    queryKey: getRpmActivityCountKey(filterWithItems),
    queryFn: async () => getActivityCountFn(filterWithItems),
    enabled: Boolean(filter.startTime || filter.endTime),
  });

  return {
    activity,
    queryKey,
    isLoading,
    isFetching,
    activityCount,
    countLoading,
  };
};
